import { isEmptyValue } from './common';
import { formatDate } from './date';

export function parseFiniteNumber(value: unknown): number | null {
  if (
    (typeof value !== 'number' && typeof value !== 'string') ||
    (typeof value === 'string' && !value.trim())
  ) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export type FormatNumberValueOptions = Intl.NumberFormatOptions & {
  defaultValue?: string;
  locale?: string;
  suffix?: string;
};

export function formatNumberValue(
  value: string | number | null | undefined,
  options: FormatNumberValueOptions = {},
): string {
  const {
    defaultValue = '-',
    locale = 'en-US',
    suffix = '',
    ...numberFormatOptions
  } = options;
  const number = parseFiniteNumber(value);
  if (number === null) return defaultValue;
  return `${new Intl.NumberFormat(locale, numberFormatOptions).format(number)}${suffix}`;
}

/**
 * Groups the integer digits of a number in threes: 1234567.5 -> 1'234'567.5.
 * The decimal part is kept as written, so "25.50" stays "25.50".
 */
export function groupDigits(value: string | number, separator = "'"): string {
  const [integerPart, decimalPart] = String(value).split('.');
  const formattedInteger = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    separator,
  );
  return decimalPart === undefined
    ? formattedInteger
    : `${formattedInteger}.${decimalPart}`;
}

export function formatValue(
  value: string | number | null | undefined,
  defaultValue = '-',
): string {
  if (isEmptyValue(value)) {
    return defaultValue;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : defaultValue;
  }

  return String(value);
}

export function formatOptionalDate(
  value: Date | string | null | undefined,
  defaultValue = '-',
): string {
  if (!value) {
    return defaultValue;
  }

  if (value instanceof Date && Number.isNaN(value.getTime())) {
    return defaultValue;
  }

  return formatDate(value);
}

export function formatWithSuffix(
  value: string | number | null | undefined,
  suffix: string,
  defaultValue = '-',
): string {
  const formatted = formatValue(value, '');
  return formatted ? `${formatted} ${suffix}` : defaultValue;
}

export function formatMoney(
  value: number | null | undefined,
  defaultValue = '-',
): string {
  return formatWithSuffix(value, '€', defaultValue);
}

export function formatDays(
  value: number | string | null | undefined,
  daysLabel: string,
  defaultValue = '-',
): string {
  return formatWithSuffix(value, daysLabel, defaultValue);
}

export function formatPercentage(
  value: number | string | null | undefined,
  defaultValue = '-',
): string {
  const formatted = formatValue(value, '');
  return formatted ? `${formatted}%` : defaultValue;
}

export function formatStatus(
  status: string | { label?: string | null } | null | undefined,
  defaultValue = '-',
): string {
  if (!status) {
    return defaultValue;
  }

  if (typeof status === 'object') {
    return formatValue(status.label, defaultValue);
  }

  return formatValue(status, defaultValue);
}

export type CountryLike = {
  id: number | string;
  ctry_name?: string | null;
  ctry_short?: string | null;
  ctry_alpha3?: string | null;
  /** Whether addresses in the country need a postcode. */
  ctry_postcode_required?: boolean | null;
};

export type CountryOption = CountryLike & {
  ctry_name: string;
};

export function formatCountryLabel(
  value: CountryLike | string | number | null | undefined,
  countries: CountryLike[],
  defaultValue = '-',
): string {
  if (!value) {
    return defaultValue;
  }

  if (typeof value === 'object') {
    return formatValue(value.ctry_name, defaultValue);
  }

  const country = countries.find((item) => String(item.id) === String(value));
  return formatValue(country?.ctry_name, defaultValue);
}
